/*!
 * @license
 * Copyright Squiz Australia Pty Ltd. All Rights Reserved.
 */

import { sortByProp } from '@squiz/dxp-console-support';
import { DataList, DataListItem, Grid, GridItem } from '@squiz/sds';
import { useSegmentEvents } from 'apps/customer-data-platform/src/Services/segmentation';
import property from 'lodash/property';
import React, { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { CDP_STRINGS } from '../../../../Constants/Strings.constants';
import { CUSTOMER_PROFILE_EMPTY_VALUE, CUSTOMER_PROFILE_GRID_ITEM_KEYS } from '../../CustomerProfile.constants';
import { ScvAttribute } from '../../attributes/ScvAttribute.defs';
import { SCV_SYSTEM_ATTRIBUTES } from '../../attributes/ScvSystemAttributes.constants';
import {
  EventOccurrenceUnit,
  GetAttributeValueType,
  getAttributeValue,
} from '../../utils/GetAttributeValue/GetAttributeValue';
import { CustomerAttributesProps } from './CustomerAttributes.props';

/**
 * The content of the attributes tab on the customer profile screen.
 */
export const CustomerAttributes = (props: CustomerAttributesProps): JSX.Element => {
  const { t } = useTranslation();
  const { customer, schema } = props;
  const { $customerid } = customer;
  const eventsList = useSegmentEvents(``);

  // Labels
  const labels = useMemo(() => {
    return {
      mappedAttributes: t(CDP_STRINGS.mappedAttributes),
      noUndefinedAttributes: t(CDP_STRINGS.noUndefinedAttributes),
      undefinedAttributes: t(CDP_STRINGS.undefinedAttributes),
    };
  }, [t]);

  // Get all available attributes (schema + system) into a single array
  // For each one, if the customer has a value, add that to the object
  const allAttributes = useMemo(() => {
    // Convert schema properties object into an array of attributes
    const schemaAttributes = Object.keys(schema.properties).map((key): ScvAttribute => {
      return {
        key,
        ...schema.properties[key],
      };
    });

    // Deep clone so that values only get assigned to this specific customer
    const systemAttributes = JSON.parse(JSON.stringify(SCV_SYSTEM_ATTRIBUTES)) as Array<ScvAttribute>;

    // Event attributes.
    const schemaEventAttributes = Object.entries(property(`x-scv-event-properties`)(schema) || {}).map(
      ([key, value]): ScvAttribute => {
        const props = property(`x-scv-event-properties.${key}`)(schema) as ScvAttribute;
        props.key = key;
        props.type = `event`;
        // Default data when there is no event list data.
        props.value = undefined;
        if (eventsList) {
          // A default value with event list data included.
          props.value = Object.entries(eventsList).reduce((acc, [, listValue]): string => {
            if (listValue.attributeid === key) {
              acc = getAttributeValue(
                {
                  relevance: parseInt(`${listValue.relevance?.value}`),
                  unit: listValue.relevance?.type as EventOccurrenceUnit,
                  value: 0,
                },
                `event`,
                t,
              );
            }
            return acc;
          }, ``);
        }
        return props;
      },
    );

    // Combine the schema/system attributes, filter out any duplicates,
    // sort alphabetically, and then add the attribute values from the customer.
    const result: Array<ScvAttribute> = [...systemAttributes, ...schemaAttributes, ...schemaEventAttributes]
      .filter((attribute, index, self) => {
        return self.indexOf(attribute) === index;
      })
      .map((attribute) => {
        const { key, type } = attribute;

        // If the attribute is defined on the /customers/{customerId} response,
        // format the attribute for display in the mapped attributes list
        if (customer[key] !== undefined) {
          let rawValue = customer[key] as GetAttributeValueType;
          if (type === `event` && eventsList) {
            Object.entries(eventsList)
              .filter(([, listValue]): boolean => {
                return listValue.attributeid === key;
              })
              .forEach(([, listValue]): void => {
                const relevanceValue = parseInt(`${listValue.relevance?.value}`);
                rawValue = {
                  // No zeros or negative numbers, default will be 1.
                  relevance: relevanceValue <= 0 ? 1 : relevanceValue,
                  unit: listValue.relevance?.type as EventOccurrenceUnit,
                  value: parseInt(`${customer[key]}`),
                };
              });
          }

          const value = getAttributeValue(rawValue, type, t);
          if (value !== CUSTOMER_PROFILE_EMPTY_VALUE) {
            attribute.value = value;
          }
        }

        return attribute;
      });

    return result;
  }, [customer, eventsList, schema, t]);

  // Get list of attributes that appear on the customer response
  // This corresponds to all attributes with a defined `value`
  const getMappedAttributes = (): Array<DataListItem> => {
    return allAttributes
      .filter((attribute) => attribute.value !== undefined)
      .filter((attribute) => attribute.value !== ``)
      .filter((attribute) => attribute.title !== undefined)
      .sort((a, b) => sortByProp(a, b, (item) => item.title.toLowerCase()))
      .map((attribute) => {
        const { key, title, value } = attribute;
        const content = (
          <div className="customer-profile-item">
            <span className="customer-profile-item__name">{title}</span>
            <span className="customer-profile-item__value">{value}</span>
          </div>
        );
        return { content, isCustom: true, key: `customer-mapped-attribute--${key}` } as DataListItem;
      });
  };

  // Get list of schema/system attributes that were NOT on the customer response
  // This corresponds to all attributes with no defined `value`
  const getUndefinedAttributes = (): Array<DataListItem> => {
    const undefinedAttributes = allAttributes
      .filter((attribute) => attribute.value === undefined || attribute.value === ``)
      .sort((a, b) => sortByProp(a, b, (item) => item.title?.toLowerCase()));

    if (undefinedAttributes.length === 0) {
      const emptyItem = {
        content: <span>{labels.noUndefinedAttributes}</span>,
        isCustom: true,
        key: `customer-undefined-attribute--empty`,
      } as DataListItem;
      return [emptyItem];
    }

    return undefinedAttributes.map((attribute) => {
      const { key, title } = attribute;
      const content = (
        <div className="customer-profile-item customer-profile-item--undefined">
          <span className="customer-profile-item__name">{title}</span>
        </div>
      );
      return { content, isCustom: true, key: `customer-undefined-attribute--${key}` } as DataListItem;
    });
  };

  const gridKey = `customer-attributes-grid--${$customerid}`;
  const gridItems: Array<GridItem> = [
    {
      component: (
        <DataList
          classes="customer-attributes__wrapper"
          empty={<></>}
          isInteractive={false}
          items={getMappedAttributes()}
          title={labels.mappedAttributes}
        />
      ),
      componentName: CUSTOMER_PROFILE_GRID_ITEM_KEYS.mappedAttributes,
      componentSize: {
        colWidth: 6,
        rowHeight: 0,
      },
      key: CUSTOMER_PROFILE_GRID_ITEM_KEYS.mappedAttributes,
    },
    {
      component: (
        <DataList
          classes="customer-attributes__wrapper"
          empty={<></>}
          isInteractive={false}
          items={getUndefinedAttributes()}
          title={labels.undefinedAttributes}
        />
      ),
      componentName: CUSTOMER_PROFILE_GRID_ITEM_KEYS.undefinedAttributes,
      componentSize: {
        colWidth: 6,
        rowHeight: 0,
      },
      key: CUSTOMER_PROFILE_GRID_ITEM_KEYS.undefinedAttributes,
    },
  ];

  if (!eventsList) {
    return <></>;
  }

  return (
    <div className="customer-details__wrapper">
      <Grid items={gridItems} key={gridKey} />
    </div>
  );
};
